import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Slider,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Calculate as CalculateIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Assessment as AssessmentIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import { startOfMonth, format } from 'date-fns';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://plateforme.fhcgroupesarl.com/api';

const getLabelColor = (label) => {
  const colors = {
    'Excellent': 'success',
    'Très Bien': 'info',
    'Bien': 'primary',
    'Satisfaisant': 'warning',
    'Passable': 'warning',
    'Insuffisant': 'error',
  };
  return colors[label] || 'default';
};

const getHeaders = () => {
  const token = localStorage.getItem('fhc_token');
  return {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const PerformanceModule = () => {
  const [tabValue, setTabValue] = useState(0);
  const [employees, setEmployees] = useState([]);
  const [performances, setPerformances] = useState([]);
  const [statistiques, setStatistiques] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateDebut, setDateDebut] = useState(startOfMonth(new Date()));
  const [dateFin, setDateFin] = useState(new Date());
  
  // Modal pour scores manuels
  const [openScoresModal, setOpenScoresModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [scoreTravailEquipe, setScoreTravailEquipe] = useState(0);
  const [scoreRespectRegles, setScoreRespectRegles] = useState(0);
  
  // Modal pour détails performance
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [detailsPerformance, setDetailsPerformance] = useState(null);
  
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    chargerDonnees();
  }, [dateDebut, dateFin]);

  const chargerDonnees = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('fhc_token');
      
      if (!token) {
        setError('Vous devez être connecté');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        date_debut: format(dateDebut, 'yyyy-MM-dd'),
        date_fin: format(dateFin, 'yyyy-MM-dd'),
      });

      const [perfResponse, statsResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/performance/tous?${params}`, { 
          headers: getHeaders() 
        }),
        fetch(`${API_BASE_URL}/performance/statistiques?${params}`, { 
          headers: getHeaders() 
        }),
      ]);

      if (!perfResponse.ok || !statsResponse.ok) {
        throw new Error('Erreur de chargement');
      }

      const perfData = await perfResponse.json();
      const statsData = await statsResponse.json();

      setPerformances(perfData.performances || []);
      setStatistiques(statsData.statistiques || null);
    } catch (err) {
      console.error('Erreur chargement performances:', err);
      setError('Erreur lors du chargement des performances');
    } finally {
      setLoading(false);
    }
  };

  const calculerPerformanceEmploye = async (employeeId) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const params = {
        date_debut: format(dateDebut, 'yyyy-MM-dd'),
        date_fin: format(dateFin, 'yyyy-MM-dd'),
      };

      const response = await fetch(
        `${API_BASE_URL}/performance/employee/${employeeId}/calculer`,
        {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify(params)
        }
      );

      if (!response.ok) throw new Error('Erreur de calcul');

      setSuccess('Performance calculée avec succès');
      await chargerDonnees();
    } catch (err) {
      console.error('Erreur calcul performance:', err);
      setError('Erreur lors du calcul de la performance');
    } finally {
      setLoading(false);
    }
  };

  const recalculerTout = async () => {
    if (!window.confirm('Recalculer les performances de tous les employés ?')) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const params = {
        date_debut: format(dateDebut, 'yyyy-MM-dd'),
        date_fin: format(dateFin, 'yyyy-MM-dd'),
      };

      const response = await fetch(
        `${API_BASE_URL}/performance/recalculer-tous`,
        {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify(params)
        }
      );

      if (!response.ok) throw new Error('Erreur de recalcul');

      const data = await response.json();
      setSuccess(data.message);
      await chargerDonnees();
    } catch (err) {
      console.error('Erreur recalcul global:', err);
      setError('Erreur lors du recalcul global');
    } finally {
      setLoading(false);
    }
  };

  const ouvrirModalScores = (employee) => {
    setSelectedEmployee(employee);
    setScoreTravailEquipe(employee.score_travail_equipe || 0);
    setScoreRespectRegles(employee.score_respect_regles || 0);
    setOpenScoresModal(true);
  };

  const sauvegarderScoresManuels = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/performance/employee/${selectedEmployee.employee_id}/scores-manuels`,
        {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify({
            score_travail_equipe: scoreTravailEquipe,
            score_respect_regles: scoreRespectRegles,
          })
        }
      );

      if (!response.ok) throw new Error('Erreur de mise à jour');

      setSuccess('Scores manuels mis à jour avec succès');
      setOpenScoresModal(false);
      await chargerDonnees();
    } catch (err) {
      console.error('Erreur mise à jour scores:', err);
      setError('Erreur lors de la mise à jour des scores');
    } finally {
      setLoading(false);
    }
  };

  const afficherDetails = async (employeeId) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        date_debut: format(dateDebut, 'yyyy-MM-dd'),
        date_fin: format(dateFin, 'yyyy-MM-dd'),
      });

      const response = await fetch(
        `${API_BASE_URL}/performance/employee/${employeeId}?${params}`,
        {
          headers: getHeaders()
        }
      );

      if (!response.ok) throw new Error('Erreur de chargement');

      const data = await response.json();
      setDetailsPerformance(data);
      setOpenDetailsModal(true);
    } catch (err) {
      console.error('Erreur chargement détails:', err);
      setError('Erreur lors du chargement des détails');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon fontSize="large" color="primary" />
            Performance & Scores des Employés
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <DatePicker
              label="Date début"
              value={dateDebut}
              onChange={setDateDebut}
              slotProps={{ textField: { size: 'small' } }}
            />
            <DatePicker
              label="Date fin"
              value={dateFin}
              onChange={setDateFin}
              slotProps={{ textField: { size: 'small' } }}
            />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={chargerDonnees}
              disabled={loading}
            >
              Actualiser
            </Button>
            <Button
              variant="contained"
              startIcon={<CalculateIcon />}
              onClick={recalculerTout}
              disabled={loading}
            >
              Recalculer Tout
            </Button>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        {/* Statistiques globales */}
        {statistiques && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Employés Évalués
                  </Typography>
                  <Typography variant="h4">{statistiques.total_employees}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Moyenne Globale
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {statistiques.moyenne_globale.toFixed(1)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Meilleur Score
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {statistiques.score_max.toFixed(1)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Score le Plus Bas
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {statistiques.score_min.toFixed(1)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Tableau des performances */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Classement des Performances
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Rang</TableCell>
                      <TableCell>Employé</TableCell>
                      <TableCell>Poste</TableCell>
                      <TableCell>Département</TableCell>
                      <TableCell>Score Global</TableCell>
                      <TableCell>Évaluation</TableCell>
                      <TableCell align="center">Présence</TableCell>
                      <TableCell align="center">Tâches</TableCell>
                      <TableCell align="center">Objectifs</TableCell>
                      <TableCell align="center">Équipe</TableCell>
                      <TableCell align="center">Règles</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {performances.map((perf, index) => (
                      <TableRow key={perf.employee_id}>
                        <TableCell>
                          {index === 0 && <TrophyIcon color="warning" />}
                          {index === 1 && <TrophyIcon sx={{ color: '#C0C0C0' }} />}
                          {index === 2 && <TrophyIcon sx={{ color: '#CD7F32' }} />}
                          {index > 2 && <Typography>{index + 1}</Typography>}
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="medium">{perf.nom}</Typography>
                        </TableCell>
                        <TableCell>{perf.poste}</TableCell>
                        <TableCell>{perf.departement}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="h6" color="primary">
                              {perf.score_global.toFixed(1)}%
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={perf.score_global}
                              sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={perf.score_label}
                            color={getLabelColor(perf.score_label)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {perf.details.presence.score.toFixed(0)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {(perf.details.taches?.score || perf.details.delai_taches?.score || 0).toFixed(0)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {perf.details.objectifs.score.toFixed(0)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {perf.details.travail_equipe.score.toFixed(0)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {perf.details.respect_regles.score.toFixed(0)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Modifier scores manuels (DG)">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => ouvrirModalScores(perf)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Voir détails">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => afficherDetails(perf.employee_id)}
                            >
                              <TrendingUpIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Recalculer">
                            <IconButton
                              size="small"
                              onClick={() => calculerPerformanceEmploye(perf.employee_id)}
                            >
                              <CalculateIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Modal pour scores manuels */}
        <Dialog open={openScoresModal} onClose={() => setOpenScoresModal(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Modifier les scores manuels - {selectedEmployee?.nom}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Typography gutterBottom>
                Travail en équipe (10%) : {scoreTravailEquipe}%
              </Typography>
              <Slider
                value={scoreTravailEquipe}
                onChange={(e, val) => setScoreTravailEquipe(val)}
                min={0}
                max={100}
                marks
                valueLabelDisplay="auto"
                sx={{ mb: 4 }}
              />

              <Typography gutterBottom>
                Respect des règles et hygiène (5%) : {scoreRespectRegles}%
              </Typography>
              <Slider
                value={scoreRespectRegles}
                onChange={(e, val) => setScoreRespectRegles(val)}
                min={0}
                max={100}
                marks
                valueLabelDisplay="auto"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenScoresModal(false)}>Annuler</Button>
            <Button
              variant="contained"
              onClick={sauvegarderScoresManuels}
              disabled={loading}
            >
              Enregistrer
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal détails performance */}
        <Dialog open={openDetailsModal} onClose={() => setOpenDetailsModal(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Détails de performance - {detailsPerformance?.employee?.nom}
          </DialogTitle>
          <DialogContent>
            {detailsPerformance && (
              <Box sx={{ pt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h5" color="primary" gutterBottom>
                      Score Global: {detailsPerformance.performance.score_global.toFixed(2)}%
                    </Typography>
                    <Chip
                      label={detailsPerformance.performance.score_label}
                      color={getLabelColor(detailsPerformance.performance.score_label)}
                    />
                  </Grid>
                  {Object.entries(detailsPerformance.performance.details).map(([key, detail]) => (
                    <Grid item xs={12} key={key}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          {key === 'presence' && 'Présence & Ponctualité'}
                          {key === 'taches' && 'Exécution des tâches'}
                          {key === 'delai_taches' && 'Délai d\'exécution des tâches'}
                          {key === 'objectifs' && 'Atteinte des objectifs'}
                          {key === 'travail_equipe' && 'Travail en équipe'}
                          {key === 'respect_regles' && 'Respect des règles et hygiène'}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ flex: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={detail.score}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                          </Box>
                          <Box sx={{ ml: 2, minWidth: 150 }}>
                            <Typography variant="body2" color="textSecondary">
                              Score: {detail.score.toFixed(1)}% | Poids: {detail.poids}%
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              Contribution: {detail.contribution.toFixed(2)}%
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDetailsModal(false)}>Fermer</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default PerformanceModule;
